import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ButtonHeading,
  CommonDatePicker,
  CommonInput,
  CommonModal,
} from "components";
import StorageService from "utils/StorageServices/StorageServices";
import toast, { Toaster } from "react-hot-toast";
import {
  deleteUser,
  getTrip,
  getTripCount,
} from "_services";
import { useNavigate } from "react-router-dom";
import { UserContext } from "context";
import MastersFilterModal from "modals/MastersFilterModal";
import Tab from "components/common/Tab";
import {
  AllDispatchDetailTab,
} from "components/DispatchDetailTabs";
import { ConfirmModalFor, EbillExtentionModal } from "modals";
import { actionPermission, errorSchema, utils } from "helper";
import images from "constants/images";
import { addEwayWill } from "features";
import { useDispatch, useSelector } from "react-redux";
import useDocumentTitle from "hooks/useDocumentTitle";
import { debounce } from "lodash";
import { constant } from "constants";
import './trip.css'
import moment from "moment";

// Constants
const ITEMS_PER_PAGE = 25;
const TRIP_LABEL = "Trip";
const DOCUMENT_TITLE = "Trip | T360";

// React Query keys
const QUERY_KEYS = {
  TRIP_LIST: 'tripList',
};

// Initial state objects
const INITIAL_EWAY_FORM = {
  eway_bill_number: "",
  start_date: new Date(),
  end_date: new Date(),
};

const INITIAL_FILTER_STATE = {
  company_id: StorageService.getCompanyId(),
  consignor_id: "",
  supplier_id: "",
  driver_id: "",
  vehicle_ownership: 0,
  from_location: "",
  to_location: "",
  branch_id: "",
  start_date: "",
  end_date: "",
  limit: ITEMS_PER_PAGE,
  offset: 1,
  search: "",
  trip_status: 0,
  vehicle_id: "",
  firm_id: ""
};

const INITIAL_LIST_DATA = {
  consignorList: [],
  branchList: [],
  supplierList: [],
  driverList: [],
  vehicleList: []
};

const Trip = ({ hideHeaderSection = true, detailsData = {} }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const prevFilterRef = useRef();

  // Context and Redux
  const {
    settingChild,
    masterData,
    tripFilters,
    allMenu,
    tripFilter,
    setTRipFilter,
    setVehicleDetails
  } = useContext(UserContext);

  const { loading, error, userData, data } = useSelector((state) => state.trip);


  // State management
  const [state, setState] = useState({
    showOffcanvas: false,
    loadingDelete: false,
    isDelete: false,
    showFilterModal: false,
    ewaybillShow: false,
    activeTab: 0,
    totalItems: 0,
    filterCount: 0,
  });



  const [tabList, setTabList] = useState([]);
  const [tripCountList, setTripCountList] = useState([]);
  const [filter, setFilter] = useState(INITIAL_FILTER_STATE);
  const [ewayForm, setEwayForm] = useState(INITIAL_EWAY_FORM);
  const [formError, setFormError] = useState("");
  const [ewayBillId, setEwayBillId] = useState("");
  const [lrId, setLrId] = useState();
  const [listData, setListData] = useState(INITIAL_LIST_DATA);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    messageType: null,
    templateId: null,
    templateData: {}
  });

  // Refs
  const prevTripFilterRef = useRef();
  const prevFilter = useRef()
  console.log("filterTrip", filter)
  // Debounced query invalidation
  const debouncedInvalidateQueries = useMemo(
    () => debounce(() => {
      queryClient.invalidateQueries(['tripList']);
    }, 300),
    [queryClient]
  );

  // React Query for Trip List
  const {
    data: tripData,
    isLoading: isTripLoading,
    isError: isTripError,
    error: tripError,
    refetch: refetchTrip,
    isFetching: isTripFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.TRIP_LIST],
    queryFn: async () => {
      if (!tripFilter?.company_id) {
        throw new Error('Company ID is required');
      }

      const formatDate = (date) => {
        if (!date) return "";
        const m = moment(date);
        return m.isValid() ? m.format("YYYY-MM-DD") : "";
      };

      const res = await getTrip({
        ...tripFilter,
        start_date: formatDate(tripFilter?.start_date),
        end_date: formatDate(tripFilter?.end_date),
        firm_id: StorageService?.getFirmId() ?? "",
      });

      if (res?.responseCode === 200) {
        return {
          data: res.data,
          totalItems: res?.data?.total_record,
        };
      } else {
        throw new Error(res?.responseMessage || 'Failed to fetch trip data');
      }
    },
    enabled: !!tripFilter?.company_id, // Only run query when company_id exists
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes

  });

  // Extract trip list and total items from React Query data
  const userlist = useMemo(() => tripData?.data || [], [tripData]);
  const totalItems = useMemo(() => tripData?.totalItems || 0, [tripData]);

  // Derived data
  const childsUser = useMemo(() =>
    allMenu?.find((i) => i?.label === TRIP_LABEL),
    [allMenu]
  );

  // Custom hooks
  useDocumentTitle(DOCUMENT_TITLE);

  // Update state helper
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Trip count fetcher (keeping original implementation)
  const getTripCountList = useCallback(async (filterParams) => {
    if (!filterParams?.company_id) return;

    const formatDate = (date) => {
      if (!date) return "";
      const m = moment(date);
      return m.isValid() ? m.format("YYYY-MM-DD") : "";
    };

    try {
      const {
        company_id,
        consignor_id,
        supplier_id,
        driver_id,
        vehicle_ownership,
        from_location,
        to_location,
        branch_id,
        start_date,
        end_date,
        vehicle_id,
        search,

      } = filterParams;

      const res = await getTripCount({
        company_id,
        consignor_id,
        supplier_id,
        driver_id,
        vehicle_ownership,
        from_location,
        to_location,
        branch_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        vehicle_id,
        search,
        firm_id: StorageService?.getFirmId() ?? ""
      });

      if (res?.responseCode === 200) {
        setTripCountList(res?.data?.trip_status);
      } else {
        toast.error("Failed to fetch trip count");
      }
    } catch (error) {
      console.error("Error fetching trip count:", error);
    }
  }, []);

  // Map counts to status
  const mapCountsToStatus = useCallback((statusList, countData) => {
    if (!Array.isArray(statusList) || !countData) return [];

    return statusList.map((statusItem) => {
      const countMapping = {
        "Completed": countData.completed,
        "At Party": countData.at_party,
        "Draft": countData.draft,
        "In Transit": countData.in_transit,
        "Delivered": countData.delivered,
        "Cancelled": countData.cancelled,
        "All": countData.all,
      };

      return {
        ...statusItem,
        count: countMapping[statusItem.name] || 0
      };
    });
  }, []);

  // Event handlers
  const handlePageChange = useCallback((page) => {
    setTRipFilter(prev => ({ ...prev, offset: page }));
  }, [setTRipFilter]);

  const handleTabChange = useCallback((tabIndex) => {
    updateState({ activeTab: tabIndex });
    setTRipFilter(prev => ({ ...prev, trip_status: tabIndex, offset: 1 }));
  }, [updateState, setTRipFilter]);

  const handleEwayFormChange = useCallback(async (name, value) => {
    const updatedForm = { ...ewayForm, [name]: value };
    setEwayForm(updatedForm);

    if (formError) {
      const validationResult = await utils.checkFormError(
        updatedForm,
        errorSchema.ewayBillSchima
      );

      setFormError(validationResult === true ? "" : validationResult);
    }
  }, [ewayForm, formError]);

  const handleEwayBillUpdate = useCallback(async () => {
    const validationResult = await utils.checkFormError(
      ewayForm,
      errorSchema.ewayBillSchima
    );

    if (validationResult !== true) {
      setFormError(validationResult);
      return;
    }

    const payload = {
      lr_id: lrId,
      eway_bill_number: ewayForm.eway_bill_number,
      eway_bill_date: ewayForm.start_date,
      eway_expiry_date: ewayForm.end_date,
    };

    try {
      const res = await dispatch(addEwayWill(payload));

      if (res?.payload?.responseCode === 200) {
        // Refetch trip data using React Query
        refetchTrip();
        getTripCountList(tripFilter);
        updateState({ ewaybillShow: false });
        setEwayForm(INITIAL_EWAY_FORM);
        toast.success("E-way bill updated successfully");
      } else {
        throw new Error(res?.payload?.responseMessage || "Failed to update E-way bill");
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, [ewayForm, lrId, dispatch, refetchTrip, getTripCountList, tripFilter, updateState]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!state.isDelete?.user_id) return;

    updateState({ loadingDelete: true });

    try {
      const object = {
        role_id: state.isDelete.user_id,
        company_id: StorageService.getCompanyId(),
      };

      const res = await deleteUser(object);

      if (res?.responseCode === 200) {
        toast.success(res.responseMessage);
        // Refetch trip data using React Query
        refetchTrip();
        getTripCountList(tripFilter);
        updateState({ loadingDelete: false, isDelete: false });
      } else {
        throw new Error(res?.message || "Failed to delete user");
      }
    } catch (error) {
      updateState({ loadingDelete: false });
      toast.error(error.message);
    }
  }, [state.isDelete, updateState, refetchTrip, getTripCountList, tripFilter]);

  const clearFilter = useCallback(() => {
    const clearedFilter = {
      ...INITIAL_FILTER_STATE,
      company_id: StorageService.getCompanyId(),
    };
    setFilter(clearedFilter)
    setTRipFilter(clearedFilter);
    updateState({ filterCount: 0 });
    setVehicleDetails({})
  }, [setTRipFilter, updateState]);

  const callBackLrId = useCallback((lrDetails) => {
    if (lrDetails?._id) {
      setLrId(lrDetails._id);
      updateState({ ewaybillShow: true });
    }
  }, [updateState]);

  const callTripList = useCallback(() => {
    // Use React Query refetch instead of manual API call
    // refetchTrip();
    getTripCountList(tripFilter);
  }, [refetchTrip, getTripCountList, tripFilter]);

  // useEffect(() => {
  //   const prevFilter = prevFilterRef.current;
  //   if (
  //     JSON.stringify(prevFilter) !== JSON.stringify(filter)
  //   ) {
  //     queryClient.invalidateQueries(['tripList']);
  //     getTripCountList(tripFilter);
  //     prevFilterRef.current = filter;
  //   }

  // }, [filter]);


  useEffect(() => {
    const prevTripFilter = prevTripFilterRef.current;
const prevFilter = prevTripFilterRef.current;
    if (
      JSON.stringify(prevTripFilter) !== JSON.stringify(tripFilter)  &&
      tripFilter?.company_id
    ) {
      const filteredValues = Object.entries(tripFilter)
        .filter(([key, val]) =>
          !["offset", 
            "limit",
            "company_id",
            "trip_status",
            "branch_name",
            "consignor_name",
            "supplier_name",
            "driver_name",
            "vehicle_no",
            "firm_id",
            "end_date",
            "search"
          ].includes(key) &&
          val !== "" &&
          val !== null &&
          val !== undefined &&
          val !== 0
        )
        .map(([_, val]) => val);

      updateState({
        filterCount: filteredValues.length,
        activeTab: tripFilter.trip_status,
      });

      console.log("tripFilter", tripFilter)
      setFilter((f) => ({
        ...tripFilter,
        search: tripFilter?.search
      }));
      queryClient.invalidateQueries(['tripList']);

      // **ONLY call getTripCountList if something OTHER than trip_status or offset changed**
      const hasNonTabFilterChange = prevTripFilter &&
        Object.keys(tripFilter).some(key => {
          // Ignore these keys when checking for changes
          if (['trip_status', 'offset', 'limit'].includes(key)) return false;
          return tripFilter[key] !== prevTripFilter[key];
        });

      // Also call on initial mount (when prevTripFilter is undefined)
      if (!prevTripFilter || hasNonTabFilterChange) {
        getTripCountList(tripFilter);
      }

      prevTripFilterRef.current = tripFilter;
      prevFilterRef.current = filter;
    }
  }, [tripFilter, updateState, getTripCountList, queryClient,filter]);

  useEffect(() => {
    if (masterData?.trip_status_list) {
      const filteredList = masterData.trip_status_list
        .filter((item) => item.name !== "Draft")
        .map((trip) => ({
          ...trip,
          id: trip.id,
          title: trip.name,
          value: trip.name,
        }));

      const allOption = {
        id: 0,
        title: "All",
        value: "All",
        name: "All",
      };

      setTabList([allOption, ...filteredList]);
    }
  }, [masterData]);

  // Handle React Query error state
  useEffect(() => {
    if (isTripError && tripError) {
      console.error("Trip fetch error:", tripError);
    }
  }, [isTripError, tripError]);

  useEffect(() => {
    // Set supplier filter when detailsData has supplier_id
    if (detailsData?._id && setTRipFilter) {
      setTRipFilter(prev => {
        // Only update if the supplier_id is different to avoid unnecessary re-renders
        if (prev.supplier_id !== detailsData._id) {
          return {
            ...prev,
            supplier_id: detailsData._id,
            offset: 1 // Reset to first page when filter changes
          };
        }
        return prev;
      });
    }
  }, [detailsData?._id, setTRipFilter]);

  // Memoized values
  const tabsWithCounts = useMemo(() => {
    return mapCountsToStatus(tabList, tripCountList);
  }, [tabList, tripCountList, mapCountsToStatus]);

  // Props objects
  const confirmModalProps = useMemo(() => ({
    isOpen: !!state.isDelete,
    closeModal: () => updateState({ isDelete: false }),
    modalTitle: "Confirmation",
    submitTitle: "Confirm",
    cancleTitle: "Close",
    onSubmit: handleDeleteConfirm,
    isLoadding: state.loadingDelete,
  }), [state.isDelete, state.loadingDelete, updateState, handleDeleteConfirm]);

  const tripProps = useMemo(() => ({
    list: userlist,
    loading: isTripLoading || isTripFetching, // Use React Query loading states
    handlePageChange,
    filter,
    totalItems,
    itemsPerPage: ITEMS_PER_PAGE,
    callBackLrId,
    callTripList,
  }), [userlist, isTripLoading, isTripFetching, totalItems, filter, handlePageChange, callBackLrId, callTripList]);

  const tabProps = useMemo(() => ({
    tabList: tabsWithCounts,
    callBackTab: handleTabChange,
    activeTab: state.activeTab,
  }), [tabsWithCounts, handleTabChange, state.activeTab]);

  const ewaybillProps = useMemo(() => ({
    show: state.ewaybillShow,
    closeModal: () => updateState({ ewaybillShow: false }),
    title: "Update",
    submitTitle: "Update",
    onSubmit: handleEwayBillUpdate,
    icon: images.EDIT,
    isLoadding: loading,
    disabled: loading,
  }), [state.ewaybillShow, updateState, handleEwayBillUpdate, loading]);

  const handlePermissionCall = async (e, link) => {
    e.preventDefault();

    await actionPermission({
      company_id: StorageService.getCompanyId(),
      type: constant?.USABILITY_CONFIG_TYPES?.TRIP,
      onSuccess: () => navigate(link),
      setModalConfig, // Pass setter
    });
  };

  let ebillExpProps = {
    isOpen: modalConfig.isOpen,
    closeModal: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
    onSubmit: (e) => setModalConfig(prev => ({ ...prev, isOpen: false })),
    modalMainClass: " _dashCommon_Modal __actionPermissionModal _smModal",
    headerPart: true,
    floatCrossBtn: true,
    // submitTitle: "Send Enquiry",
    messageType: modalConfig.messageType,
    templateId: modalConfig.templateId,
    templateData: modalConfig.templateData
  };

  return (
    <div>
      {hideHeaderSection && <ButtonHeading
        showSearchFilter={{
          search: true,
          filterOption: true,
          filterCount: true,
        }}
        onClickFilterModal={() => updateState({ showFilterModal: !state.showFilterModal })}
        setFilter={setFilter}
        title={TRIP_LABEL}
        title2={TRIP_LABEL}
        onClick={(e) => handlePermissionCall(e, "/trip/addtrip/createtrip2")}
        // onClick2={(e) => handlePermissionCall(e ,"/trip/addtrip/createtrip2")}
        buttonTitle={childsUser?.create === 1 ? "Add Trip" : ""}
        //  buttonTitle2={childsUser?.create === 1 ? "Add Trip" : ""} 
        filterCount={state.filterCount}
        clearFilter={clearFilter}
        filter={filter}
        isDebouncedSearch={true}
      />}

      <div className="addPr_flBx __trpFlx_main">
        {tabsWithCounts.length > 0 && <Tab {...tabProps} />}

        <AllDispatchDetailTab {...tripProps} />

      </div>

      <div
        className={`offcanvas offcanvas-end common_offCanvas ${state.showFilterModal ? "show" : ""
          }`}
        tabIndex={-1}
        style={{ visibility: state.showFilterModal ? "visible" : "hidden" }}
      >
        {state.showFilterModal && (
          <MastersFilterModal
            isOpen={state.showFilterModal}
            onClose={() => updateState({ showFilterModal: false })}
            filter={filter}
            setFilter={setFilter}
            isShow={{
              dateRange: true,
              allBranch: true,
              consignor: true,
              supplier: true,
              fromLocation: true,
              toLocation: true,
              driver: true,
              ownership: true,
              vehicle: true,
              search:false
            }}
           filterCount={state.filterCount}
            setFilterCount={(count) => updateState({ filterCount: state.filterCount })}
            name={TRIP_LABEL}
          />
        )}
      </div>

      <div className={state.showFilterModal ? "offBack_drop" : ""}></div>

      {state.isDelete && (
        <CommonModal {...confirmModalProps}>
          <p className="p-3">
            Are you sure you want to delete {state.isDelete.company_user_name}?
          </p>
        </CommonModal>
      )}

      {state.ewaybillShow && (
        <ConfirmModalFor {...ewaybillProps}>
          <div className="colFlx">
            <div className="_addRouts_flex">
              <CommonDatePicker
                name="start_date"
                label="Date of Dispatch"
                startDate={ewayForm.start_date}
                sedatetStartDate={(data) => handleEwayFormChange("start_date", data)}
                error={formError?.start_date}
                focus={formError?.start_date}
              />
              <CommonDatePicker
                name="end_date"
                label="Expiry Date"
                startDate={ewayForm.end_date}
                setStartDate={(data) => handleEwayFormChange("end_date", data)}
                error={formError?.end_date}
                focus={formError?.end_date}
              />
            </div>
            <CommonInput
              label="Eway bill number"
              placeholder="Enter Number"
              name="eway_bill_number"
              onChange={({ target: { name, value } }) => {
                if (/^\d*$/.test(value)) {
                  handleEwayFormChange(name, value);
                }
              }}
              maxLength={12}
              value={ewayForm.eway_bill_number}
              error={formError?.eway_bill_number}
              focus={formError?.eway_bill_number}
            />
          </div>
        </ConfirmModalFor>
      )}

      {modalConfig.isOpen && <EbillExtentionModal

        {...ebillExpProps}

      />}
    </div>
  );
};

export default Trip;